import { type ComponentProps, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { type BagFeaturedUpsellCard } from '../../components';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useBagFeaturedUpsellContent = (params: Params) => {
  const { pause } = params;

  const { formatMessage } = useIntl();

  const { data } = useContentfulContentTypeEntry<BagFeaturedUpsellFields>({
    contentType: BAG_FEATURED_UPSELL_CONTENT_TYPE,
    pause,
  });

  const fields = data?.fields;

  // ─── Derived ───────────────────────────────────────────────────────────────

  return useMemo<BagFeaturedUpsellContent | undefined>(() => {
    const imageUrl = getAssetUrl(fields?.image);

    if (!fields?.productLineageKey || !fields.headline || !imageUrl) {
      return;
    }

    return {
      productLineageKey: fields.productLineageKey,
      eyebrow: fields.eyebrow,
      headline: fields.headline,
      addCta: fields.addCta,
      dismissCta: fields.dismissCta ?? formatMessage(messages.dismissCta),
      imageUrl,
      imageA11y: fields.image?.fields?.description,
      badgeImageUrl: getAssetUrl(fields.badgeImage),
      badgeImageA11y: fields.badgeImage?.fields?.description,
      animationImageUrl: getAssetUrl(fields.animationImage),
    };
  }, [fields, formatMessage]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAssetUrl(asset: Asset | undefined) {
  return asset?.fields?.file?.url;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BAG_FEATURED_UPSELL_CONTENT_TYPE = 'bagFeaturedUpsell';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dismissCta: {
    defaultMessage: 'No thanks',
    description: 'Bag | Featured upsell | Dismiss CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  pause: boolean;
};

type BagFeaturedUpsellContent = ComponentProps<typeof BagFeaturedUpsellCard>['content'] & {
  productLineageKey: string;
  addCta?: string;
  animationImageUrl?: string;
};

type BagFeaturedUpsellFields = {
  productLineageKey?: string;
  eyebrow?: string;
  headline?: string;
  addCta?: string;
  dismissCta?: string;
  image?: Asset;
  badgeImage?: Asset;
  animationImage?: Asset;
};
